/**
 * Pure helpers for CUSTOMER interactive shop map (Fix 8).
 * No React / Leaflet imports — keep unit tests hermetic.
 */

/** WGS84 [lat, lng] — Prague, matches admin SalesPointGeoPicker. */
export const CZECHIA_FALLBACK_CENTER: readonly [number, number] = [50.0755, 14.4378];

/** Default Leaflet zoom when centering on fallback / single point (admin DEFAULT_ZOOM). */
export const DEFAULT_MAP_ZOOM = 13;

/** Inclusive WGS84 latitude range. */
const LAT_MIN = -90;
const LAT_MAX = 90;
/** Inclusive WGS84 longitude range. */
const LNG_MIN = -180;
const LNG_MAX = 180;

export type ValidLatLng = {
  readonly lat: number;
  readonly lng: number;
};

export type GeoPointLike = {
  readonly lat: number | null;
  readonly lng: number | null;
};

/** Minimal map-row shape: id + nullable coords (PublicCustomerSalesPointDTO compatible). */
export type ShopMapGeoItem = GeoPointLike & {
  readonly salesPointId: number;
  /** ONLINE_ONLY is never plottable even if leftover coords leak (A11b). */
  readonly customerPresence?: 'STOREFRONT' | 'ONLINE_ONLY' | string | null;
};

export type ShopMapItemWithCoords<T extends ShopMapGeoItem = ShopMapGeoItem> = T & ValidLatLng;

/** Leaflet-compatible bounds corners: [[south, west], [north, east]]. */
export type LatLngBoundsCorners = readonly [
  readonly [number, number],
  readonly [number, number],
];

/**
 * True when both values are finite WGS84 numbers in range.
 * Rejects null, undefined, NaN, ±Infinity, and out-of-range.
 */
export function validLatLng(
  lat: number | null | undefined,
  lng: number | null | undefined,
): boolean {
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    return false;
  }
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return false;
  }
  if (lat < LAT_MIN || lat > LAT_MAX) {
    return false;
  }
  if (lng < LNG_MIN || lng > LNG_MAX) {
    return false;
  }
  return true;
}

/** Type guard: object has finite in-range `lat` and `lng`. */
export function hasValidLatLng<T extends GeoPointLike>(
  point: T,
): point is T & ValidLatLng {
  return validLatLng(point.lat, point.lng);
}

/** Extract `{ lat, lng }` when valid; otherwise `null`. */
export function extractValidLatLng(point: GeoPointLike): ValidLatLng | null {
  if (!hasValidLatLng(point)) {
    return null;
  }
  return { lat: point.lat, lng: point.lng };
}

/**
 * Filter directory rows to those plottable on the map.
 * Invalid / missing coords and ONLINE_ONLY (A11b) are dropped — remain available in list mode.
 */
export function buildMapItems<T extends ShopMapGeoItem>(
  items: readonly T[],
): Array<ShopMapItemWithCoords<T>> {
  return items.filter(
    (item): item is ShopMapItemWithCoords<T> =>
      item.customerPresence !== 'ONLINE_ONLY' && hasValidLatLng(item),
  );
}

/**
 * Stable signature of the plottable set so fitBounds runs only when
 * the filtered dataset meaningfully changes (ids + coords), not on every render.
 */
export function computeFitBoundsKey(
  items: readonly Pick<ShopMapItemWithCoords, 'salesPointId' | 'lat' | 'lng'>[],
): string {
  if (items.length === 0) {
    return '0|';
  }
  const parts = items
    .map((item) => `${item.salesPointId}:${item.lat}:${item.lng}`)
    .sort();
  return `${parts.length}|${parts.join(';')}`;
}

/** Stable Leaflet marker key — remount when coords change (live admin geo save). */
export function salesPointMapMarkerKey(
  salesPointId: number,
  lat: number,
  lng: number,
): string {
  return `${salesPointId}:${lat}:${lng}`;
}

/**
 * Bounding box for Leaflet `fitBounds`, or `null` when there are no points.
 */
export function latLngBoundsFromItems(
  items: readonly ValidLatLng[],
): LatLngBoundsCorners | null {
  if (items.length === 0) {
    return null;
  }

  const first = items[0];
  if (first === undefined) {
    return null;
  }

  let south = first.lat;
  let north = first.lat;
  let west = first.lng;
  let east = first.lng;

  for (let i = 1; i < items.length; i += 1) {
    const point = items[i];
    if (point === undefined) {
      continue;
    }
    const { lat, lng } = point;
    if (lat < south) {
      south = lat;
    }
    if (lat > north) {
      north = lat;
    }
    if (lng < west) {
      west = lng;
    }
    if (lng > east) {
      east = lng;
    }
  }

  return [
    [south, west],
    [north, east],
  ];
}
